/**
 * Centralized React Query Keys for cache invalidation
 *
 * These keys are used with useMutationWithHandler's reactQueryKeys prop
 * to automatically invalidate queries after mutations.
 *
 * Uses 'contains' mode for pattern matching, so:
 * - 'contracts' will match: 'contracts.list', 'contracts.customers', 'contracts.detail.123', etc.
 * - 'services' will match: 'services.poc', 'services.waiting', 'services.detail', etc.
 */

export const QUERY_KEYS = {
    PURCHASING_PAYMENTS: 'purchasing-payments',
    // Contract related
    CONTRACTS: 'contracts',
    // VNNIC domain price list (components/ProductConfig/DomainVNNIC). Same
    // value as the literal it replaced, so the cache key is unchanged.
    DOMAIN_PRICES: 'domain_prices',
    CONTRACTS_CUSTOMERS: 'contracts.customers',
    CONTRACTS_CUSTOMERS_OVERVIEW: 'contracts.customers.overview',
    CONTRACTS_PARTNERS: 'contracts.partners',
    CONTRACTS_PARTNERS_SUBCONTRACTS_CODE:
        'contracts.partners.subcontracts.code',
    CONTRACTS_SAMPLES: 'contracts.samples',
    CONTRACTS_SAMPLES_MAPPING: 'contracts.samples.mapping',
    CONTRACTS_SUBCONTRACTS_CODE: 'contracts.subcontracts.code',
    CONTRACTS_SUGGESTIONS: 'contracts.suggestions',
    CONTRACTS_STATISTICAL: 'contracts.statistical',
    CONTRACTS_PROCESSES: 'contracts.processes',
    CONTRACT: 'contract',
    CONTRACT_DETAIL: 'contracts.detail',
    SUBCONTRACTS: 'subcontracts',
    SUBCONTRACT: 'subcontract',
    SUBCONTRACTS_PROCESSES: 'subcontracts.processes',
    PROCESSES: 'processes',
    PROCESSES_HISTORIES: 'processes.histories',
    PROCESSES_APPROVALS: 'processes.approvals',
    PROCESSES_ESIGN: 'processes.esign',
    NEGOTIATIONS: 'negotiations',

    // Suppliers (Partners)
    SUPPLIERS: 'suppliers',
    SUPPLIER: 'supplier',

    // Service related
    SERVICES: 'services',
    SERVICES_POC: 'services.poc',
    SERVICES_WAITING: 'services.waiting',
    SERVICES_DEVICE_LIQUIDATION: 'services.device-liquidation',
    SERVICES_STATEMENT: 'services.statement',
    SERVICES_CONFIGOPTIONS: 'services.configoptions',
    SERVICES_CONFIGOPTIONS_PRICES: 'services.configoptions.prices',
    SERVICES_DATES: 'services.dates',
    SERVICES_INFORMATIONS: 'services.informations',
    SERVICES_METADATA: 'services.metadata',
    SERVICES_NOTIFICATIONS: 'services.notifications',
    SERVICES_NOTIFICATIONS_MAILERS: 'services.notifications.mailers',
    SERVICES_ALERTS: 'services.alerts',
    SERVICES_ANALYTICS_MONITORING: 'services.analytics.monitoring',
    SERVICES_CONTRACTS: 'services.contracts',
    SERVICES_MONITORING: 'services.monitoring',
    SERVICES_MONITORING_CDN: 'services.monitoring.cdn',
    SERVICES_SUMMARY: 'services.summary',
    SERVICES_CLOUD_INFO: 'services.cloud.info',
    SERVICES_QUOTAS: 'services.quotas',
    SERVICES_QUOTA_VIEWERS: 'services.quota.viewers',
    SERVICES_KUBERNETES_NODEPOOLS: 'services.kubernetes.nodepools',
    SERVICES_ADDON_PRICES: 'services.addon.prices',
    SERVICES_AVAILABLE_ADDONS: 'services.available.addons',
    SERVICES_PRICES: 'services.prices',
    SERVICES_VAT_RATES: 'services.vat.rates',
    SERVICES_CONFIGOPTION_PRICE_HISTORY: 'services.configoption.price-history',
    SERVICES_PRICE_CHANGE_REQUESTS: 'services.price-change-requests',
    SERVICE: 'service',
    SERVICE_DETAIL: 'services.detail',
    SERVICE_NOTIFICATION_PREVIEW: 'services.notification.preview',

    // SLA related
    SLA_TEMPLATES: 'sla.templates',
    SLA_TEMPLATE_DETAIL: 'sla.templates.detail',
    SLA_TEMPLATES_ACTIVE: 'sla.templates.active',
    SERVICE_SLA: 'services.sla',
    SERVICE_SLA_DETAIL: 'services.sla.detail',
    SERVICE_SLA_HISTORY: 'services.sla.history',
    SLA_CHANGES: 'sla.changes',
    SLA_DOWNTIME: 'sla.downtime',
    SLA_DOWNTIME_ONGOING: 'sla.downtime.ongoing',
    SLA_UPTIME_STATS: 'sla.uptime.stats',
    SLA_MONTHLY_SUMMARY: 'sla.monthly.summary',
    // SLA Ticket Compliance
    SLA_TICKETS_SUMMARY: 'sla.tickets.summary',
    SLA_TICKETS_AT_RISK: 'sla.tickets.at-risk',
    SLA_TICKETS_APPROACHING: 'sla.tickets.approaching',
    // SLA Compliance Dashboard
    SLA_COMPLIANCE_OVERVIEW: 'sla.compliance.overview',
    SLA_COMPLIANCE_CUSTOMERS: 'sla.compliance.customers',
    SLA_COMPLIANCE_AT_RISK: 'sla.compliance.at-risk',
    SLA_DASHBOARD_OVERVIEW: 'sla.dashboard.overview',
    SLA_DASHBOARD_SERVICES: 'sla.dashboard.services',
    SLA_DASHBOARD_TRENDS: 'sla.dashboard.trends',
    SLA_SERVICE_TRENDS: 'sla.service.trends',
    SLA_SERVICE_TICKETS: 'sla.service.tickets',
    SLA_SERVICE_TICKET_SUMMARY: 'sla.service.tickets.summary',

    // Customer related
    CUSTOMERS: 'customers',
    CUSTOMERS_JOURNEYS: 'customers.journeys',
    CUSTOMERS_CONTACTS: 'customers.contacts',
    CUSTOMER_LOGS: 'customer.logs',
    CUSTOMER_ACTIVITY_LOGS: 'customer.activity-logs',
    CUSTOMERS_CONTRACTS_LIST: 'customers.contracts.list',
    CUSTOMERS_CREDITS: 'customers.credits',
    CUSTOMERS_CREDITS_SERVICES: 'customers.credits.services',
    CUSTOMERS_INVOICES: 'customers.invoices',
    CUSTOMERS_MAILERS: 'customers.mailers',
    CUSTOMERS_NEGOTIATIONS_LIST: 'customers.negotiations.list',
    // Quotations of a journey (sale-process board): list, detail, one version.
    CUSTOMERS_QUOTATIONS: 'customers.quotations',
    CUSTOMERS_QUOTATION_DETAIL: 'customers.quotations.detail',
    CUSTOMERS_QUOTATION_VERSION: 'customers.quotations.version',
    // Catalog reads of the quotation form. No `customers` in them on purpose:
    // invalidation matches by substring, and every customer mutation would
    // refetch the catalog otherwise.
    QUOTATION_PRODUCT_GROUPS: 'quotation.product-groups',
    QUOTATION_PRODUCTS: 'quotation.products',
    QUOTATION_PRODUCT_DETAIL: 'quotation.product-detail',
    CUSTOMER: 'customers',
    CUSTOMER_DETAIL: 'customers.detail',
    CUSTOMER_JOURNEY: 'customers.journey',
    CUSTOMER_JOURNEY_LIST: 'customers.journey.list',
    JOURNEYS: 'journeys',
    CONTACTS: 'contact',
    CONTACT_SOURCE_DETAIL: 'contacts.source.detail',
    CONTACT_SOURCE_REVENUE: 'contacts.source.revenue',
    ACCOUNT_STATUS: 'account.status',
    CUSTOMER_ADDRESS: 'customers.address',
    CUSTOMER_REVENUE_COMBINED: 'customers.revenue.combined',

    // Invoice related
    INVOICES: 'invoices',
    INVOICES_WAITING: 'invoices.waiting',
    INVOICES_PAYMENT: 'invoices.payment',
    INVOICES_STATISTICAL: 'invoices.statistical',
    INVOICE_PREFILL_EMAIL: 'invoices.prefill.email',
    INVOICE_PREVIEW: 'invoices.preview',
    INVOICE_DETAIL: 'invoices.detail',
    INVOICE_ITEMS: 'invoices.items',
    INVOICE_TRANSACTIONS: 'invoices.transactions',
    INVOICE_LOGS: 'invoices.logs',
    INVOICE_UPLOADED_DOCUMENTS: 'invoices.uploaded-documents',
    INVOICE_ACCOUNTING_RETURNS: 'invoices.accounting-returns',
    INVOICE_MAIL_HISTORY: 'invoices.mail.history',
    INVOICE_REVENUE_ALLOCATION: 'invoices.revenue.allocation',
    // Chứng từ ký số (giấy đề nghị thanh toán, biên bản đối soát); key đi kèm loại chứng từ.
    // Preview / images cố ý không bắt đầu bằng 'invoices.signed-document' để invalidate kiểu
    // contains theo INVOICE_SIGNED_DOCUMENT không kéo theo chúng.
    INVOICE_SIGNED_DOCUMENT: 'invoices.signed-document',
    INVOICE_SIGNED_DOCUMENT_PREVIEW: 'invoices.preview.signed-document',
    INVOICE_SIGNED_DOCUMENT_IMAGES: 'invoices.images.signed-document',
    // Hoá đơn điện tử (MISA meInvoice)
    INVOICE_E_INVOICE: 'invoices.e-invoice',
    INVOICE_E_INVOICE_BUYER_LOGS: 'invoices.e-invoice.buyer-logs',
    INVOICE_SIGNED_DOCUMENT_MAIL_PREFILL:
        'invoices.prefill.signed-document-mail',
    // Employee related
    EMPLOYEES: 'employees',
    EMPLOYEE: 'employees',
    CURRENT_USER_PROFILE: 'current-user-profile',
    EMPLOYEE_CURRENT_INFO: 'employees.current-infor',
    EMPLOYEE_KPI_TARGETS: 'employees.kpi-targets',
    EMPLOYEE_UPDATE_ADVANCED: 'employees.update.advanced',
    EMPLOYEE_DASHBOARD: 'employees.dashboard',
    EMPLOYEE_CAREER_PATHS: 'employees.career-paths',
    EMPLOYEE_EVENTS: 'employees.events',
    EMPLOYEE_REFERRAL_LINK: 'employees.referral-link',
    ACTIVITY_TARGETS: 'activity-targets',
    ACTIVITY_ACTUALS: 'activity-actuals',
    DEPARTMENTS: 'departments',
    POSITIONS: 'positions',

    // Ticket related
    TICKETS: 'tickets',
    TICKETS_PUBLIC: 'tickets.public',
    TICKETS_INTERNAL: 'tickets.internal',
    TICKET_DETAIL: 'tickets.detail',
    TICKET_ATTACHMENT: 'tickets.attachment',
    TICKET_COMMENT: 'tickets.comment',
    TICKET_DASHBOARD: 'tickets.dashboard',
    // Must stay under the `tickets.` prefix: invalidation is a substring match
    // on the joined key, so a name outside that namespace would never be
    // invalidated by ticket mutations.
    TICKET_ESCALATED_DETAILS: 'tickets.escalated-details',
    TICKET_ITIL_OVERVIEW: 'tickets.itil.overview',
    TICKET_ITIL_LIST: 'tickets.itil.list',
    TICKET_TYPES: 'tickets.types',
    TICKET_REQUESTS: 'tickets.requests',
    TICKET_FEEDBACK_TOKENS: 'tickets.feedback.tokens',
    TICKET_FEEDBACKS: 'tickets.feedbacks',
    INTERNAL: 'internal',

    // Technical Dashboard
    TECHNICAL_TICKET_SUMMARY: 'technical.ticket.summary',
    TECHNICAL_TICKETS: 'technical.tickets',
    TECHNICAL_UNASSIGNED_TICKETS: 'technical.unassigned.tickets',

    // Lead Statistics
    LEAD_STATISTICS: 'lead.statistics',
    LEAD_STATISTICS_BY_WEBSITE: 'lead.statistics.by-website',
    LEAD_QUICK_VIEW: 'lead.quick-view',

    // Livechat's other keys: @vnetwork/datacentral-livechat/query-keys (#216).
    // This one is also read by Marketing's conversation preview, so it stays
    // here with its exact value.
    LIVECHAT_MESSAGES: 'livechat.messages',

    // Business' keys live in the package: @vnetwork/datacentral-business/query-keys
    // Dashboard
    DASHBOARD_BUSINESSES: 'dashboard.businesses',
    DASHBOARD_REVENUE_TARGETS: 'dashboard.revenue.targets',
    DASHBOARD_REVENUE_TARGETS_WITH_ACTUALS:
        'dashboard.revenue.targets.with.actuals',
    DASHBOARD_MONTHLY_REVENUE: 'monthly-revenue',
    DASHBOARD_ANALYTICS: 'dashboard-analytics',
    DASHBOARD_DATA: 'dashboard-data',

    // Documents &
    ASSETS: 'assets',
    ASSET_DETAIL: 'asset-detail',
    ASSET_VERSIONS: 'asset-versions',
    ASSETS_DASHBOARD: 'assets-dashboard',
    ASSETS_DRAFT: 'assets-draft',
    DOCUMENTS: 'documents',
    DOCUMENT: 'document',
    DOCUMENTS_SHARE_REQUESTS: 'documents-share-requests',
    // DRIVE, HEIC_PREVIEW and SHARED_WITH_ME are also in the Drive package's own
    // keys (packages/drive: constants/query-keys.ts), with the same values; the
    // rest of Drive's block moved there.
    DRIVE: 'drive',
    SHARED_OBJECT: 'sharedObject',
    SHARED_WITH_ME: 'sharedWithMe',
    HEIC_PREVIEW: 'heic-preview',
    // Marketing & Mailer
    MARKETING: 'marketing',
    GROUP_MAILER_TYPES: 'group-mailer-types',
    MAILER_CAMPAIGNS: 'mailer-campaigns',
    MAILER_CAMPAIGNS_DETAIL: 'mailer-campaigns.detail',
    MAILER_TEMPLATES: 'mailer-templates',
    MAILER_TEMPLATES_DETAIL: 'mailer-templates.detail',
    MAILER_INTERNAL_SOURCES: 'mailer-internal-sources',

    // Activities & Notifications
    ACTIVITIES: 'activities',
    NOTIFICATIONS: 'notifications',
    NOTIFICATION_STATISTIC: 'notificationStatistic',

    // Authorization & Permissions
    AUTHORIZATION: 'authorization',
    AUTHORIZATION_SCOPES: 'authorization.scopes',
    AUTHORIZATION_RESOURCES: 'authorization.resources',
    AUTHORIZATION_PERMISSIONS: 'authorization.permissions',
    AUTHORIZATION_POLICIES: 'authorization.policies',
    AUTHORIZATION_KEYCLOAK_RESOURCES: 'authorization.keycloak.resources',
    AUTHORIZATION_KEYCLOAK_GROUPS: 'authorization.keycloak.groups',
    SCOPES: 'scopes',
    DATA_ACCESS_ASSIGNMENTS: 'data-access-assignments',
    POLICIES: 'policies',
    PERMISSIONS: 'permissions',
    ROLE_PERMISSIONS: 'role-permissions',
    RESOURCES: 'resources',
    RESOURCES_SCOPES: 'resources-scopes',
    KEYCLOAK_RESOURCES: 'keycloak-resources',

    // HR - Roles & Holidays
    ROLES: 'roles',
    HOLIDAYS: 'holidays',
    HOLIDAY_DETAIL: 'holiday-detail',

    // HR - Jobs & Resumes
    RESUMES: 'resumes',
    CV_DETAIL: 'cv-detail',
    JOBS: 'jobs',
    JOB_DETAIL: 'job-detail',
    JOB_CATEGORIES: 'job-categories',
    JOB_CATEGORY_DETAIL: 'job-category-detail',
    JOB_CATEGORIES_LIST: 'jobCategories',
    // HR - Interview Schedule
    INTERVIEW_SCHEDULE: 'interview-schedule',
    // HR - Late Employees
    LATE_EMPLOYEES: 'late-employees',
    // My Recent Workdays
    MY_RECENT_WORKDAYS: 'my-recent-workdays',

    // HR - KPI & Workdays
    KPI: 'kpi',
    DEFAULT_KPI_TARGETS: 'default-kpi-targets',
    WORKDAYS: 'workdays',
    WORKDAYS_TOTAL: 'total-workdays',
    WORKDAYS_REPORT: 'workdays-report',
    TOTAL_WORKDAY: 'total-workday',
    ATTENDANCE_SYNC_LOGS: 'attendance-sync-logs',
    ATTENDANCE_SYNC_LOG_METADATA: 'attendance-sync-log-metadata',

    // HR - VCards (Dayoffs' keys live in the package: @vnetwork/datacentral-dayoffs/query-keys)
    VCARDS: 'vcards',
    VCARD_CURRENT_EMPLOYEE: 'vcard-current-employee',

    // Reports & Analytics
    REPORTS_CUSTOMERS_ANALYTICS: 'reports.customers.analytics',
    REPORTS_CUSTOMERS_ANALYTICS_INDUSTRY:
        'reports.customers.analytics.industry',
    REPORTS_CUSTOMERS_ANALYTICS_SERVICE: 'reports.customers.analytics.service',
    REPORTS_CUSTOMERS_STATIS: 'reports.customers.statis',
    REPORTS_RECEIVABLES: 'reports.receivables',
    REPORTS_RECEIVABLES_SUMMARY: 'reports.receivables.summary',
    REPORTS_RECEIVABLE_CUSTOMER: 'reports.receivables.customer',
    REPORTS_RECEIVABLE_NOTE_LOGS: 'reports.receivables.note-logs',
    REPORTS_INCOMES: 'reports.incomes',
    REPORTS_INCOMES_STATS: 'reports.incomes.stats',
    REPORTS_VPBANK_APPLICATIONS: 'reports.vpbank-applications',

    // Internal rating (đánh giá nội bộ liên phòng — ticket-only Phase 3)
    CUSTOMER_CALENDAR_EVENTS: 'customers.calendar-events',
    RATINGS_TICKET: 'ratings.ticket',
    RATINGS_AWAITING: 'ratings.awaiting',
    RATINGS_RECEIVED: 'ratings.received',
    STATICS: 'statics',

    // BI Reports - Revenue
    REPORTS_BI_INVOICES: 'reports.bi.invoices',
    REPORTS_BI_INVOICE_DETAIL: 'reports.bi.invoices.detail',
    REPORTS_BI_REVENUE_ALLOCATIONS: 'reports.bi.revenue.allocations',
    REPORTS_BI_REVENUE_SUMMARY: 'reports.bi.revenue.summary',
    REPORTS_BI_REVENUE_MONTHLY: 'reports.bi.revenue.monthly',
    REPORTS_BI_REVENUE_COST: 'reports.bi.revenue.costs',
    REPORTS_BI_REVENUE_PRODUCT_GROUPS: 'reports.bi.revenue.productgroups',
    REPORTS_BI_REVENUE_BY_CUSTOMER: 'reports.bi.revenue.bycustomer',
    REPORTS_BI_REVENUE_BY_PAYMENT_DATE: 'reports.bi.revenue.bypaymentdate',

    // BI Reports - Service Costs
    REPORTS_BI_SERVICE_COSTS: 'reports.bi.service.costs',
    REPORTS_BI_SERVICE_COST_DETAIL: 'reports.bi.service.costs.detail',
    REPORTS_BI_SERVICE_COSTS_SUMMARY: 'reports.bi.service.costs.summary',
    // Read-only views next to Service costs (Warehouse infrastructure, ISP purchases)
    REPORTS_BI_INFRASTRUCTURE_COSTS: 'reports.bi.infrastructure.costs',
    REPORTS_BI_ISP_COSTS: 'reports.bi.isp.costs',

    // BI Reports - Other Financials
    REPORTS_BI_OTHER_FINANCIALS: 'reports.bi.other.financials',

    // BI Reports - Operation Expenses
    REPORTS_BI_OPERATION_EXPENSES: 'expenses',

    // BI Reports - Business Performance
    REPORTS_BI_BUSINESS_PERFORMANCE: 'reports.bi.business.performance',
    REPORTS_BI_BUSINESS_PERFORMANCE_COMPARISON:
        'reports.bi.business.performance.comparison',
    REPORTS_BI_PROFITABILITY_RATIOS: 'reports.bi.profitability.ratios',

    // BI Reports - Financial Ratios
    REPORTS_BI_FINANCIAL_STRENGTH_RATIOS:
        'reports.bi.financial.strength.ratios',
    REPORTS_BI_MANAGEMENT_EFFICIENCY_RATIOS:
        'reports.bi.management.efficiency.ratios',
    REPORTS_BI_OPERATING_CAPABILITY_RATIOS:
        'reports.bi.operating.capability.ratios',

    // BI Reports - Balance Sheet
    REPORTS_BI_BALANCE_SHEET_SUMMARY: 'reports.bi.balance.sheet.summary',
    REPORTS_BI_BALANCE_SHEET_MONTHLY: 'reports.bi.balance.sheet.monthly',

    // Report WAF module
    REPORT_WAF: 'reports-waf',
    REPORT_WAF_HISTORY: 'report-history',
    PIPELINE_STATS: 'pipeline-stats',
    PIPELINE_TASKS: 'pipeline-tasks',

    // Reports - Business Targets
    REPORTS_BUSINESS_TARGETS: 'reports.business.targets',

    // Reports - Sale Performances
    REPORTS_SALE_PERFORMANCES: 'reports.sale.performances',
    REPORTS_TARGETS_ANALYTICS_CUSTOMER_MOVEMENTS:
        'reports.targets-analytics.customer-movements',

    // Reports - Service Monthly Report
    REPORTS_BI_SERVICE_MONTHLY: 'reports.bi.service.monthly',

    // Reports - Product Group Report
    PRODUCT_GROUP_REPORT: 'reports.bi.product.group',

    // Products
    PRODUCTS: 'products',
    PRODUCT: 'product',
    PRODUCT_GROUPS: 'product.groups',
    PRODUCT_GROUPS_LIST: 'productGroups',
    SERVICE_INTERESTING: 'service.interesting',
    PROVIDERS: 'providers',

    // Settings
    TECHNICAL_TEMPLATES: 'technical.templates',
    REGULATIONS: 'regulations',
    REGULATIONS_LIST: 'regulations.list',
    REGULATIONS_STATS: 'regulations.stats',
    REGULATIONS_NOT_ACCEPTED: 'regulations.not-accepted',
    REGULATIONS_ACCEPTANCE_LIST: 'regulations.acceptance-list',
    PRODUCT_GROUP_VAT_RATES: 'product.group.vat.rates',
    PRODUCT_PRICES: 'product.prices',
    PRODUCT_CONFIG_OPTIONS: 'product.config.options',
    PRODUCT_PRICING_STEPS: 'product.pricing.steps',
    PRODUCTS_SEARCH: 'products.search',

    // Search & Suggestions
    SEARCH_GLOBAL: 'globalSearch',
    SEARCH_SUGGESTIONS: 'searchSuggestions',
    SEARCH_POPULAR_SEARCHES: 'popularSearches',
    SEARCH_RECENT_SEARCHES: 'recentSearches',
    POPULAR_SEARCHES: 'popularSearches',
    RECENT_SEARCHES: 'recentSearches',

    // Location
    CITIES: 'cities',
    DISTRICTS: 'districts',
    WARDS: 'wards',

    // Events
    EVENTS: 'events',
    EVENTS_POPULAR: 'events.popular',
    EVENTS_ROOM_AVAILABLE: 'events.roomAvailable',
    EVENTS_SUMMARY: 'events.summary',

    // Other
    CREDITS: 'credits',
    COMPANIES: 'companies',
    ASSISTANTS: 'assistants',
    SALE_TASK: 'sale-task',
    REQUIREMENTS: 'requirements',
    WARNINGS: 'warnings',
    POC_SERVICE_WARNINGS: 'warnings.poc-services',
    CONTRACT_WARNINGS: 'warnings.contracts',
    WHOIS: 'whois',
    DATA: 'data',

    // Survey's other keys: @vnetwork/datacentral-survey/query-keys (#216).
    // This one is also read by the Dashboard's SLA widget, so it stays here
    // with its exact value.
    SURVEY_ANALYTICS_DASHBOARD: 'surveys.analytics.dashboard',
    // SMTP related
    SMTP: 'smtp',

    // On-Call Management
    ONCALL_SCHEDULES: 'oncall-schedules',
    ONCALL_SCHEDULE_DETAIL: 'oncall-schedule',
    ONCALL_ROTATIONS: 'oncall-rotations',
    ONCALL_ROTATION_DETAIL: 'oncall-rotation',
    ONCALL_ROTATION_MEMBERS: 'oncall-rotation-members',
    ONCALL_CYCLE_TEMPLATE: 'oncall-cycle-template',
    ONCALL_OVERRIDES: 'oncall-overrides',
    ONCALL_ASSIGNMENTS: 'oncall-assignments',
    ONCALL_ASSIGNMENT_DETAIL: 'oncall-assignment',
    ONCALL_ASSIGNMENTS_CURRENT: 'oncall-current',
    ONCALL_ASSIGNMENTS_TODAY: 'oncall-today',
    ONCALL_ASSIGNMENTS_MY: 'oncall-assignments-my',
    ONCALL_SWAPS: 'oncall-swaps',
    ONCALL_SWAP_DETAIL: 'oncall-swap',
    ONCALL_SWAPS_PENDING: 'oncall-swaps-pending',
    ONCALL_SWAPS_MY: 'oncall-swaps-my',
    ONCALL_COMPENSATIONS: 'oncall-compensations',
    ONCALL_COMPENSATION_DETAIL: 'oncall-compensation',
    ONCALL_COMPENSATION_REPORT: 'oncall-compensation-report',
    ONCALL_COMPENSATIONS_MY: 'oncall-compensations-my',
    ONCALL_LOGS: 'oncall-logs',

    // Resellers: the keys live in the package: @vnetwork/datacentral-reseller/query-keys

    // Affiliates
    AFFILIATES: 'affiliates',
    AFFILIATE_DETAIL: 'affiliates.detail',
    AFFILIATE_STATS: 'affiliates.stats',
    AFFILIATE_CLICKS: 'affiliates.clicks',
    AFFILIATE_CONVERSIONS: 'affiliates.conversions',
    AFFILIATE_PAYOUTS: 'affiliates.payouts',
    AFFILIATE_PENDING_INVOICES: 'affiliates.pending-invoices',
    AFFILIATE_COMMISSIONS: 'affiliates.commissions',
    AFFILIATE_COMMISSION_POLICIES: 'affiliates.commission-policies',

    // Promos
    PROMOS: 'promos',
    PROMO_DETAIL: 'promos.detail',
    PROMO_USES: 'promos.uses',

    // Generic (use sparingly)
    GENERIC: 'generic',

    // Watchpoint Module - Healthcheck Monitoring
    WATCHPOINT_DASHBOARD: 'watchpoint.dashboard',
    WATCHPOINT_LIST: 'watchpoint.list',
    WATCHPOINT_DETAIL: 'watchpoint.detail',
    WATCHPOINT_METRICS: 'watchpoint.metrics',
    WATCHPOINT_STATS: 'watchpoint.stats',
    WATCHPOINT_ALARMS: 'watchpoint.alarms',
    WATCHPOINT_ALARM_DETAIL: 'watchpoint.alarms.detail',
    WATCHPOINT_ALARM_CHANNELS: 'watchpoint.alarms.channels',
    WATCHPOINT_ALARM_EVENTS: 'watchpoint.alarm-events',
    WATCHPOINT_ALARM_EVENT_COUNT: 'watchpoint.alarm-event-count',
    WATCHPOINT_CHANNELS: 'watchpoint.channels',
    WATCHPOINT_CHANNEL_DETAIL: 'watchpoint.channels.detail',
    WATCHPOINT_SUBSCRIBERS: 'watchpoint.subscribers',
    WATCHPOINT_SUBSCRIBER_DETAIL: 'watchpoint.subscriber-detail',
    WATCHPOINT_PLAYBOOKS: 'watchpoint.playbooks',
    WATCHPOINT_PLAYBOOK_DETAIL: 'watchpoint.playbooks.detail',

    // Threshold Module - ELK Threshold Monitoring
    THRESHOLD_LIST: 'threshold.list',
    THRESHOLD_DETAIL: 'threshold.detail',
    THRESHOLD_ELASTIC_METADATA: 'threshold.elastic-metadata',
    THRESHOLD_ELASTIC_DOMAINS: 'threshold.elastic-domains',
    THRESHOLD_ALARMS: 'threshold.alarms',
    THRESHOLD_PLAYBOOKS: 'threshold.playbooks',
    THRESHOLD_PLAYBOOK_ACTIONS: 'threshold.playbook-actions',

    // Tools
    WAF_FREE_REPORT: 'tools.waf-free-report',

    // Operations (read-only cross-org inventory)
    OPS_RESOURCES: 'operations.resources',
    OPS_RESOURCE_SUMMARY: 'operations.resource-summary',
    OPS_SERVICES: 'operations.services',
    OPS_SERVICES_SUMMARY: 'operations.services-summary',
    OPS_SERVICE: 'operations.service',
    OPS_SERVICE_USAGE: 'operations.service-usage',
    OPS_DASHBOARD: 'operations.dashboard',
    OPS_CDN: 'operations.cdn',
    OPS_ORGS: 'operations.orgs',
    OPS_GROUPED_ROWS: 'operations.grouped-rows',
    OPS_PRODUCT_GROUPS: 'operations.product-groups',
} as const

// Type helper for autocomplete
export type QueryKey = (typeof QUERY_KEYS)[keyof typeof QUERY_KEYS]

// Commonly used combinations
export const QUERY_KEY_COMBINATIONS = {
    // Contract operations often affect customers
    CONTRACT_WITH_CUSTOMER: [QUERY_KEYS.CONTRACTS, QUERY_KEYS.CUSTOMERS],

    // Service operations often affect tickets
    SERVICE_WITH_TICKET: [QUERY_KEYS.SERVICES, QUERY_KEYS.TICKETS],

    // Service operations can affect invoices
    SERVICE_WITH_INVOICE: [QUERY_KEYS.SERVICES, QUERY_KEYS.INVOICES],

    // Contract operations can affect services
    CONTRACT_WITH_SERVICE: [QUERY_KEYS.CONTRACTS, QUERY_KEYS.SERVICES],

    // Customer operations can affect contracts
    CUSTOMER_WITH_CONTRACT: [QUERY_KEYS.CUSTOMERS, QUERY_KEYS.CONTRACTS],

    // Process operations affect contracts
    PROCESS_WITH_CONTRACT: [QUERY_KEYS.PROCESSES, QUERY_KEYS.CONTRACTS],

    // Subcontract operations affect both
    SUBCONTRACT_WITH_PROCESS: [QUERY_KEYS.PROCESSES, QUERY_KEYS.SUBCONTRACTS],

    // Employee operations can affect documents
    EMPLOYEE_WITH_DOCUMENT: [QUERY_KEYS.EMPLOYEES, QUERY_KEYS.DOCUMENTS],

    // Mailer campaign operations can affect mailer campaigns
    SMTP: [QUERY_KEYS.MAILER_CAMPAIGNS_DETAIL, 'test_smtp_connection'],

    // SLA Downtime operations affect uptime stats and monthly summary
    SLA_DOWNTIME_WITH_STATS: [
        QUERY_KEYS.SLA_DOWNTIME,
        QUERY_KEYS.SLA_UPTIME_STATS,
        QUERY_KEYS.SLA_MONTHLY_SUMMARY,
        QUERY_KEYS.SLA_DASHBOARD_OVERVIEW,
        QUERY_KEYS.SLA_DASHBOARD_SERVICES,
        QUERY_KEYS.SLA_DASHBOARD_TRENDS,
        QUERY_KEYS.SLA_SERVICE_TRENDS,
        QUERY_KEYS.SLA_DOWNTIME_ONGOING,
    ],
} as const
